<script setup lang="ts">
import { ref, reactive } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useToast } from 'primevue/usetoast'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Password from 'primevue/password'
import AppCard from '@/components/ui/AppCard.vue'
import Message from 'primevue/message'

const authStore = useAuthStore()
const toast = useToast()

const activeTab = ref<'profile' | 'password'>('profile')
const loading = ref(false)
const error = ref<string | null>(null)
const success = ref<string | null>(null)

const profileForm = reactive({
  name: authStore.user?.name ?? '',
  email: authStore.user?.email ?? '',
})

const passwordForm = reactive({
  current_password: '',
  password: '',
  password_confirmation: '',
})

async function updateProfile(): Promise<void> {
  error.value = null
  success.value = null
  loading.value = true

  try {
    await authStore.updateProfile(profileForm)
    success.value = 'Perfil actualizado correctamente'
    toast.add({ severity: 'success', summary: 'Éxito', detail: success.value, life: 3000 })
  } catch (err: unknown) {
    const axiosError = err as { apiError?: { message: string } }
    error.value = axiosError.apiError?.message ?? 'Error al actualizar el perfil'
    toast.add({ severity: 'error', summary: 'Error', detail: error.value, life: 4000 })
  } finally {
    loading.value = false
  }
}

async function changePassword(): Promise<void> {
  error.value = null
  success.value = null
  loading.value = true

  if (passwordForm.password !== passwordForm.password_confirmation) {
    error.value = 'Las contraseñas no coinciden'
    loading.value = false
    return
  }

  try {
    await authStore.changePassword(passwordForm.current_password, passwordForm.password)
    success.value = 'Contraseña cambiada correctamente'
    toast.add({ severity: 'success', summary: 'Éxito', detail: success.value, life: 3000 })
    passwordForm.current_password = ''
    passwordForm.password = ''
    passwordForm.password_confirmation = ''
  } catch (err: unknown) {
    const axiosError = err as { apiError?: { message: string } }
    error.value = axiosError.apiError?.message ?? 'Error al cambiar la contraseña'
    toast.add({ severity: 'error', summary: 'Error', detail: error.value, life: 4000 })
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="space-y-6 animate-fade-in max-w-2xl mx-auto">
    <div>
      <h1 class="text-2xl font-semibold text-surface-900 dark:text-surface-100">
        Mi perfil
      </h1>
      <p class="text-surface-500 dark:text-surface-400 mt-1">
        Gestiona tu información personal y seguridad
      </p>
    </div>

    <AppCard>
      <template #header>
        <div class="flex flex-col sm:flex-row sm:items-center gap-3">
          <div class="flex items-center gap-3">
            <Avatar
              :label="authStore.user?.name"
              size="xlarge"
              shape="circle"
              class="bg-primary-100 dark:bg-primary-900/30"
            />
            <div>
              <p class="text-lg font-semibold text-surface-900 dark:text-surface-100">
                {{ authStore.user?.name }}
              </p>
              <p class="text-sm text-surface-500 dark:text-surface-400">
                {{ authStore.user?.username }}
              </p>
            </div>
          </div>
        </div>
      </template>

      <!-- Tabs -->
      <div class="border-b border-surface-200 dark:border-surface-700 mb-6">
        <nav
          class="flex gap-1"
          aria-label="Tabs del perfil"
        >
          <!--
            `type="button"` explícito: un `<button>` sin `type` es `submit`, y
            aunque hoy estas pestañas no están dentro de un `<form>`, el HTML no lo
            comprueba y el día que se enclose una, guarda sin querer.
          -->
          <button
            type="button"
            :class="[
              'px-4 py-3 text-sm font-medium rounded-t-lg transition-colors border-b-2 -mb-px',
              activeTab === 'profile'
                ? 'text-primary-600 dark:text-primary-400 border-primary-600 dark:border-primary-400'
                : 'text-surface-500 dark:text-surface-400 hover:text-surface-700 dark:hover:text-surface-300 border-transparent'
            ]"
            @click="activeTab = 'profile'"
          >
            Perfil
          </button>
          <!--
            `type="button"` explícito: un `<button>` sin `type` es `submit`, y
            aunque hoy estas pestañas no están dentro de un `<form>`, el HTML no lo
            comprueba y el día que se enclose una, guarda sin querer.
          -->
          <button
            type="button"
            :class="[
              'px-4 py-3 text-sm font-medium rounded-t-lg transition-colors border-b-2 -mb-px',
              activeTab === 'password'
                ? 'text-primary-600 dark:text-primary-400 border-primary-600 dark:border-primary-400'
                : 'text-surface-500 dark:text-surface-400 hover:text-surface-700 dark:hover:text-surface-300 border-transparent'
            ]"
            @click="activeTab = 'password'"
          >
            Seguridad
          </button>
        </nav>
      </div>

      <!-- Tab Perfil -->
      <div
        v-if="activeTab === 'profile'"
        class="space-y-5 animate-fade-in"
      >
        <Message
          v-if="error"
          severity="error"
          :closable="true"
        >
          {{ error }}
        </Message>
        <Message
          v-if="success"
          severity="success"
          :closable="true"
        >
          {{ success }}
        </Message>

        <form
          class="space-y-5"
          @submit.prevent="updateProfile"
        >
          <div>
            <label
              for="name"
              class="label-base"
            >Nombre completo</label>
            <InputText
              id="name"
              v-model="profileForm.name"
              :disabled="loading"
              fluid
              autocomplete="name"
            />
          </div>

          <div>
            <label
              for="email"
              class="label-base"
            >Correo electrónico</label>
            <InputText
              id="email"
              v-model="profileForm.email"
              type="email"
              :disabled="loading"
              fluid
              autocomplete="email"
              placeholder="opcional"
            />
          </div>

          <div class="pt-4 border-t border-surface-200 dark:border-surface-700">
            <Button
              type="submit"
              :loading="loading"
              :disabled="loading"
              label="Guardar cambios"
            />
          </div>
        </form>
      </div>

      <!-- Tab Contraseña -->
      <div
        v-if="activeTab === 'password'"
        class="space-y-5 animate-fade-in"
      >
        <Message
          v-if="error"
          severity="error"
          :closable="true"
        >
          {{ error }}
        </Message>
        <Message
          v-if="success"
          severity="success"
          :closable="true"
        >
          {{ success }}
        </Message>

        <form
          class="space-y-5"
          @submit.prevent="changePassword"
        >
          <div>
            <label
              for="current_password"
              class="label-base"
            >Contraseña actual</label>
            <Password
              id="current_password"
              v-model="passwordForm.current_password"
              :disabled="loading"
              fluid
              autocomplete="current-password"
              :toggle-mask="true"
            />
          </div>

          <div>
            <label
              for="password"
              class="label-base"
            >Nueva contraseña</label>
            <Password
              id="password"
              v-model="passwordForm.password"
              :disabled="loading"
              fluid
              autocomplete="new-password"
              :toggle-mask="true"
            />
            <p class="text-xs text-surface-500 dark:text-surface-400 mt-1">
              Mínimo 8 caracteres
            </p>
          </div>

          <div>
            <label
              for="password_confirmation"
              class="label-base"
            >Confirmar nueva contraseña</label>
            <Password
              id="password_confirmation"
              v-model="passwordForm.password_confirmation"
              :disabled="loading"
              fluid
              autocomplete="new-password"
              :toggle-mask="true"
            />
          </div>

          <div class="pt-4 border-t border-surface-200 dark:border-surface-700">
            <Button
              type="submit"
              :loading="loading"
              :disabled="loading"
              label="Cambiar contraseña"
              severity="secondary"
            />
          </div>
        </form>
      </div>
    </AppCard>
  </div>
</template>